import { useMemo } from "react";
import type { MigrationRequiredPeriod } from "@common/helpers/migrationRequiredPeriod";
import { CoADocumentMigrationSource } from "@common/schemas/accounting-integration.schema";
import {
    type SubmitDocumentMigrationParams,
    useValidateDocumentMigration,
} from "@api/accountingIntegration.api";
import { matchMigrationFiles } from "@components/accounting/migrationFileMatching";
import type {
    MigrationDoc,
    MigrationMatchedRow,
    MigrationUnverifiedDoc,
} from "@components/accounting/migrationFileMatching.types";
import {
    applyCoaCrossFileRejection,
    applyMigrationIssues,
} from "@components/accounting/migrationIssues";
import { getMigrationDocumentIds } from "@components/accounting/migrationDocumentIds";

const MISSING_BASIS_WARNING_DESCRIPTION =
    "Some accounting books won't be imported. This can't be undone — each entity allows only one migration.";
const MISSING_BASIS_AND_START_DATE_WARNING_DESCRIPTION =
    "Some uploaded data doesn't line up with the migration period, and some accounting books won't be imported. This can't be undone — each entity allows only one migration.";

interface UseMigrationFinalizeGatingParams {
    workspaceId: string;
    entityId: number;
    source: CoADocumentMigrationSource;
    docs: MigrationDoc[];
    requiredPeriod: MigrationRequiredPeriod | null;
    hasAccrualLedger: boolean;
    isAccrualLedgerResolved: boolean;
    isAnyExtracting: boolean;
    isCompleted: boolean;
}

interface MigrationFinalizeGating {
    displayRows: MigrationMatchedRow[];
    unverifiedDocs: MigrationUnverifiedDoc[];
    matchedCount: number;
    migrationDocumentIds: SubmitDocumentMigrationParams | null;
    finalizeWarningMessages: string[];
    finalizeWarningDescription: string | undefined;
    finalizeDisabledReason: string | undefined;
    canFinalize: boolean;
    hasFinalizeWarnings: boolean;
    hasStartDateWarning: boolean;
    earliestGlStart: string | null;
    refetchValidation: () => void;
}

export function useMigrationFinalizeGating({
    workspaceId,
    entityId,
    source,
    docs,
    requiredPeriod,
    hasAccrualLedger,
    isAccrualLedgerResolved,
    isAnyExtracting,
    isCompleted,
}: UseMigrationFinalizeGatingParams): MigrationFinalizeGating {
    const {
        rows,
        unverifiedDocs,
        isCoaCovered,
        uncoveredBasisLabels,
        isBasisSelectionRequired,
        finalizeWarnings,
        earliestGlStart,
    } = useMemo(
        () =>
            matchMigrationFiles({
                requiredPeriod,
                hasAccrualLedger,
                isAccrualLedgerResolved,
                docs,
            }),
        [requiredPeriod, hasAccrualLedger, isAccrualLedgerResolved, docs],
    );

    const finalizeWarningMessages = useMemo(
        () => finalizeWarnings.map((warning) => warning.message),
        [finalizeWarnings],
    );
    const finalizeWarningDescription = useMemo(() => {
        const hasMissingBasis = finalizeWarnings.some(
            (warning) => warning.kind === "missingBasis",
        );
        if (!hasMissingBasis) {
            return undefined;
        }
        const hasStartDate = finalizeWarnings.some(
            (warning) => warning.kind === "startDate",
        );
        return hasStartDate
            ? MISSING_BASIS_AND_START_DATE_WARNING_DESCRIPTION
            : MISSING_BASIS_WARNING_DESCRIPTION;
    }, [finalizeWarnings]);

    const matchedCount = useMemo(() => {
        const ids = new Set<number>();
        for (const row of rows) {
            if (row.documentId != null) {
                ids.add(row.documentId);
            }
        }
        return ids.size;
    }, [rows]);

    const migrationDocumentIds = useMemo(
        () => getMigrationDocumentIds(rows),
        [rows],
    );

    const areClientGatesReady =
        !isAnyExtracting &&
        isAccrualLedgerResolved &&
        isCoaCovered &&
        !isBasisSelectionRequired &&
        uncoveredBasisLabels.length === 0 &&
        migrationDocumentIds !== null;

    const validationQuery = useValidateDocumentMigration({
        workspaceId,
        entityId,
        source,
        documentIds: migrationDocumentIds,
        enabled: areClientGatesReady && !isCompleted,
    });

    const issues = useMemo(
        () => validationQuery.data?.issues ?? [],
        [validationQuery.data],
    );
    const coaCrossFileRejection = validationQuery.data?.coaCrossFileRejection;
    const isValidating =
        areClientGatesReady && !isCompleted && validationQuery.isFetching;
    const hasValidationError =
        areClientGatesReady && !isCompleted && validationQuery.isError;

    const finalizeDisabledReason = useMemo<string | undefined>(() => {
        if (isAnyExtracting) {
            return "Wait for all files to finish processing.";
        }
        if (!isAccrualLedgerResolved) {
            return "Checking the entity's ledgers…";
        }
        if (!isCoaCovered) {
            return "Upload a chart of accounts first.";
        }
        if (isBasisSelectionRequired) {
            return "Choose a basis for each general ledger.";
        }
        if (uncoveredBasisLabels.length > 0) {
            return "Upload general ledgers covering the full period.";
        }
        if (isValidating) {
            return "Checking the uploaded files…";
        }
        if (hasValidationError) {
            return "Couldn't validate the uploaded files. Re-check them and try again.";
        }
        if (issues.length > 0 || coaCrossFileRejection != null) {
            return "Resolve the flagged files before completing.";
        }
        return undefined;
    }, [
        isAnyExtracting,
        isAccrualLedgerResolved,
        isCoaCovered,
        isBasisSelectionRequired,
        uncoveredBasisLabels,
        isValidating,
        hasValidationError,
        issues,
        coaCrossFileRejection,
    ]);

    const displayRows = useMemo(
        () =>
            applyCoaCrossFileRejection({
                rows: applyMigrationIssues({ rows, issues }),
                rejection: coaCrossFileRejection,
            }),
        [rows, issues, coaCrossFileRejection],
    );

    return {
        displayRows,
        unverifiedDocs,
        matchedCount,
        migrationDocumentIds,
        finalizeWarningMessages,
        finalizeWarningDescription,
        finalizeDisabledReason,
        canFinalize: finalizeDisabledReason === undefined,
        hasFinalizeWarnings: finalizeWarnings.length > 0,
        hasStartDateWarning: finalizeWarnings.some(
            (warning) => warning.kind === "startDate",
        ),
        earliestGlStart,
        refetchValidation: validationQuery.refetch,
    };
}
